import React, { useState, useEffect, useRef, useCallback, useContext } from "react";
import "../../App.css";
import axios from "axios";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";

import WebSocketService from "../../context/WebSocketService.js";

import ConversationArea from "./ConversationArea.jsx";
import { GlobalContext } from "../../context/GlobalContext.js";
import { useWebSocket } from "../../context/WebSocketContext";

import SidePanel from "../../components/SidePanel/SidePanel.js";
import ConversationSidePanelContent from "./ConversationSidePanelContent.js";
import MessageInput from "./MessageInput.js";
import ScenarioGoals from "./ScenarioGoals.js";
import DemoController from "./DemoController";

axios.defaults.withCredentials = true;

const INITIAL_SCENARIO_STATE = {
    reasonForLearning: "",
    additionalNotes: "",
    languageLearnerRole: "",
    conversationPartnerRole: "",
    informationToGive: "",
    informationToRequest: "",
    context: "",
};

const createEmptyConversationDetails = () => ({
    conversationId: null,
    conversationHistory: [],
    persona: "",
});

function ConversationPage() {
    const [conversationDetails, setConversationDetails] = useState(() =>
        createEmptyConversationDetails()
    );
    const [scenarioState, setScenarioState] = useState(() => ({
        ...INITIAL_SCENARIO_STATE,
    }));

    const [conversationHistory, setConversationHistory] = useState([]);
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const { auth, preferences, setPreferences } = useContext(GlobalContext);
    const { connectionStatus } = useWebSocket();

    const menuButtonRef = useRef(null);
    const messageInputRef = useRef(null);
    const currentConversationIdRef = useRef(conversationDetails.conversationId);
    const clearConversationState = useCallback(
        ({ closePanel = false } = {}) => {
            if (messageInputRef.current && typeof messageInputRef.current.reset === "function") {
                messageInputRef.current.reset();
            }
            setConversationDetails(createEmptyConversationDetails());
            setConversationHistory([]);
            setScenarioState({ ...INITIAL_SCENARIO_STATE });
            if (closePanel) {
                setIsPanelOpen(false);
            }
        },
        [setConversationDetails, setConversationHistory, setScenarioState, setIsPanelOpen]
    );
    const urlParams = new URLSearchParams(window.location.search);
    const isDemo = urlParams.has("demo");

    useEffect(() => {
        currentConversationIdRef.current = conversationDetails.conversationId;
    }, [conversationDetails.conversationId]);

    const togglePanel = useCallback(() => {
        setIsPanelOpen((prevIsOpen) => !prevIsOpen);
    }, []);

    useEffect(() => {
        if (preferences.theme) {
            import(`../../themes/${preferences.theme}.css`)
                .then(() => {})
                .catch((err) => {
                    console.error(`Failed to load ${preferences.theme} theme`, err);
                });
            document.body.className = preferences.theme;
        }
    }, [preferences.theme]);

    // Helper to convert VAPID public key from base64 to UInt8Array.
    function urlB64ToUint8Array(base64String) {
        const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
        const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }

    useEffect(() => {
        if (Capacitor.getPlatform() !== "web") {
            // Native push registration (Android/iOS)
            PushNotifications.requestPermissions().then((permission) => {
                if (permission.receive === "granted") {
                    PushNotifications.register();
                } else {
                    console.warn("Native push notifications permission not granted");
                }
            });

            // Listen for native push events
            PushNotifications.addListener("registration", (token) => {
                console.log("Native push token:", token.value);
                // Send native token to your backend for storage
                axios
                    .post(`${process.env.REACT_APP_API_URL}/notifications/register`, {
                        token: token.value,
                    })
                    .catch((error) =>
                        console.error("Error registering native push token:", error)
                    );
            });

            PushNotifications.addListener("registrationError", (error) => {
                console.error("Error during native push registration:", error);
            });

            PushNotifications.addListener("pushNotificationReceived", (notification) => {
                console.log("Native push notification received:", notification);
                // Process the notification as needed
            });

            PushNotifications.addListener(
                "pushNotificationActionPerformed",
                (notification) => {
                    console.log("Native push notification action performed:", notification);
                    // For example, navigate the user to a specific page
                }
            );
        } else {
            // Fallback to web push notifications using service worker
            if ("serviceWorker" in navigator && "PushManager" in window) {
                navigator.serviceWorker
                    .register("/sw.js")
                    .then((registration) => {
                        return registration.pushManager
                            .getSubscription()
                            .then((subscription) => {
                                if (subscription) return subscription;
                                const convertedVapidKey = urlB64ToUint8Array(
                                    process.env.REACT_APP_VAPID_PUBLIC_KEY
                                );
                                return registration.pushManager.subscribe({
                                    userVisibleOnly: true,
                                    applicationServerKey: convertedVapidKey,
                                });
                            });
                    })
                    .then((subscription) => {
                        axios
                            .post(`${process.env.REACT_APP_API_URL}/notifications/register`, {
                                subscription,
                            })
                            .catch((error) =>
                                console.error(
                                    "Error registering web push subscription:",
                                    error
                                )
                            );
                    })
                    .catch((err) => console.error("Service Worker registration failed:", err));
            }
        }
    }, []);

    // Single, safe effect. Only sends when WS is OPEN. No implicit resets.
    useEffect(() => {
        if (connectionStatus !== WebSocket.OPEN) return;

        if (conversationDetails.conversationId) {
            WebSocketService.sendMessage({
                type: "subscribe",
                conversationId: conversationDetails.conversationId,
            });
        } else {
            WebSocketService.sendMessage({ type: "unsubscribe" });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connectionStatus, conversationDetails.conversationId]);

    // Populate conversationHistory from parent prop
    useEffect(() => {
        // Retrieve new history from conversationDetails or default to an empty array.
        const newServerHistory =
            conversationDetails && Array.isArray(conversationDetails.conversationHistory)
                ? conversationDetails.conversationHistory
                : [];

        // Map new server messages to your local format.
        const newFormattedHistory = newServerHistory.map((message) => {
            const {
                id,
                targetLanguageMessage,
                originalMessage,
                morphemeTranslationDetails,
                fullSentenceTranslation,
            } = message;
            // Helper to structure each field
            const structureField = (value) => ({
                status: "loaded",
                statusMessage: null,
                value,
            });
            return {
                id,
                senderId: message.senderId,
                senderType: message.senderType,
                // Optionally include other properties...
                ...(targetLanguageMessage && {
                    targetLanguageMessage: structureField(targetLanguageMessage),
                }),
                ...(originalMessage && {
                    originalMessage: structureField(originalMessage),
                }),
                ...(morphemeTranslationDetails && {
                    morphemeTranslationDetails: structureField(morphemeTranslationDetails),
                }),
                ...(fullSentenceTranslation && {
                    fullSentenceTranslation: structureField(fullSentenceTranslation),
                }),
            };
        });

        // Preserve current temporary messages (e.g., those with id starting with "temp-")
        const tempMessages = conversationHistory.filter((msg) => msg.id.startsWith("temp-"));

        // Merge: replace all non-temp messages with the new history, but keep the temp ones.
        setConversationHistory([...newFormattedHistory, ...tempMessages]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationDetails]);

    // track fetch state across auth transitions
    const hasFetchedConversationHistoryRef = useRef(false);
    const shouldRefetchOnAuthRef = useRef(false);

    const prevWsStatusRef = useRef(connectionStatus);
    useEffect(() => {
        if (prevWsStatusRef.current !== connectionStatus) {
            // Trigger a refetch on connect, reconnect, or disconnect
            hasFetchedConversationHistoryRef.current = false;
            prevWsStatusRef.current = connectionStatus;
        }
    }, [connectionStatus]);

    useEffect(() => {
        const isAuthed = Boolean(auth?.isAuthenticated);
        const hasAccessToken = Boolean(auth?.accessToken);

        if (!isAuthed || !hasAccessToken) {
            // Defer until auth is ready
            shouldRefetchOnAuthRef.current = true;
            return;
        }

        if (hasFetchedConversationHistoryRef.current && !shouldRefetchOnAuthRef.current) {
            // Already fetched and no auth-driven refetch requested
            return;
        }

        const fetchConversationHistory = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/conversation/request-history`
                );
                //console.log("response", response);
                const { conversationDetails, preferences } = response.data;
                const {
                    _id: conversationId,
                    conversationHistory,
                    scenario,
                    ...otherDetails
                } = conversationDetails;

                setConversationDetails({
                    conversationId,
                    conversationHistory,
                    ...otherDetails,
                });
                setScenarioState(scenario);
                setPreferences(preferences);

                hasFetchedConversationHistoryRef.current = true;
                shouldRefetchOnAuthRef.current = false;
            } catch (error) {
                if (error?.response?.status === 401 || error?.response?.status === 403) {
                    shouldRefetchOnAuthRef.current = true;
                }
                console.error("Error fetching conversation history:", error);
            }
        };

        fetchConversationHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth?.isAuthenticated, auth?.accessToken, connectionStatus]);

    useEffect(() => {
        const handleMessage = (data) => {
            if (data.deletedConversationId) {
                if (currentConversationIdRef.current === data.deletedConversationId) {
                    clearConversationState();
                }
            }
            if (data.conversationDetails) {
                const {
                    _id: conversationId,
                    conversationHistory,
                    scenario,
                    ...otherDetails
                } = data.conversationDetails;
                setConversationDetails({
                    conversationId,
                    conversationHistory,
                    ...otherDetails,
                });
                setScenarioState(scenario);
            }
            if (data.preferences) {
                setPreferences(data.preferences);
            }
        };

        WebSocketService.on("message", handleMessage);

        return () => {
            WebSocketService.off("message", handleMessage);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleResetConversation = async () => {
        clearConversationState({ closePanel: true });
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/conversation/reset`);
        } catch (error) {
            console.error("Error resetting conversation:", error);
        }
    };

    return (
        <div className={preferences.theme}>
            <div className="app">
                <div className="app-container">
                    <header className="app-header">
                        <span className="navigation-menu-button">
                            <md-icon-button ref={menuButtonRef} onClick={togglePanel}>
                                <md-icon>menu</md-icon>
                            </md-icon-button>
                        </span>
                    </header>
                    {scenarioState.languageLearnerRole !== "" && (
                        <ScenarioGoals scenarioState={scenarioState} />
                    )}
                    <SidePanel
                        isOpen={isPanelOpen}
                        togglePanel={togglePanel}
                        navigationItems={[
                            { label: "Home", path: "/home" },
                            { label: "Word Bank", path: "/wordBank" },
                            { label: "Progress Dashboard", path: "/progressDashboard" },
                            ...(auth.userType === "admin"
                                ? [{ label: "Admin Page", path: "/admin" }]
                                : []),
                        ]}
                        menuButtonRef={menuButtonRef}
                        className="app-side-panel"
                        preferences={preferences}
                    >
                        <ConversationSidePanelContent
                            conversationDetails={conversationDetails}
                            setConversationDetails={setConversationDetails}
                            scenarioState={scenarioState}
                            setScenarioState={setScenarioState}
                            handleResetConversation={handleResetConversation}
                            isPanelOpen={isPanelOpen}
                            setIsPanelOpen={setIsPanelOpen}
                        />
                    </SidePanel>
                    <ConversationArea
                        preferences={preferences}
                        setPreferences={setPreferences}
                        conversationHistory={conversationHistory}
                        setConversationHistory={setConversationHistory}
                        conversationDetails={conversationDetails}
                    />
                    <MessageInput
                        ref={messageInputRef}
                        conversationDetails={conversationDetails}
                        conversationHistory={conversationHistory}
                        setConversationHistory={setConversationHistory}
                        preferences={preferences}
                    />
                    {isDemo && (
                        <DemoController
                            setConversationHistory={setConversationHistory}
                            inputRef={messageInputRef}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

export default ConversationPage;
